import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaChartLine, FaClock, FaCalendarAlt, FaUsers, FaTimesCircle } from "react-icons/fa";
import api from "../../services/api";
import "./admin.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const AdminAttendanceChart = () => {
  const [employees, setEmployees] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Filters
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Track if employee was manually selected by user
  const userSelectedRef = useRef(false);

  const getToken = () => localStorage.getItem("token");

  // ==================== Fetch Employees ====================
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        const token = getToken();
        const response = await api.get("/admin/get_employees", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 200) {
          const activeEmployees = (response.data.employees || []).filter(
            (emp) => {
              const status =
                emp.status !== undefined && emp.status !== null
                  ? parseInt(emp.status)
                  : 1;
              return status === 1;
            }
          );
          setEmployees(activeEmployees);

          if (activeEmployees.length > 0) {
            setSelectedEmployeeId(activeEmployees[0].id);
          }
        }
      } catch (error) {
        setErrorMsg(
          error.response?.data?.message || "Failed to fetch employees"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // ==================== Filtered Employees ====================
  const filteredEmployees = useMemo(() => {
    if (roleFilter === "all") return employees;
    return employees.filter(
      (emp) => String(emp.role || "").toLowerCase() === roleFilter
    );
  }, [employees, roleFilter]);

  // Reset employee only when role filter CHANGES
  const prevRoleFilterRef = useRef(roleFilter);
  useEffect(() => {
    if (prevRoleFilterRef.current === roleFilter) return;
    prevRoleFilterRef.current = roleFilter;

    if (filteredEmployees.length === 0) {
      setSelectedEmployeeId("");
      return;
    }
    const stillValid = filteredEmployees.some(
      (emp) => emp.id === selectedEmployeeId
    );
    if (!stillValid) {
      setSelectedEmployeeId(filteredEmployees[0].id);
    }
  }, [roleFilter, filteredEmployees, selectedEmployeeId]);

  // ==================== Fetch Attendance for Selected Employee ====================
  useEffect(() => {
    let isCancelled = false;

    const fetchAttendance = async () => {
      if (!selectedEmployeeId) {
        setAttendanceRecords([]);
        return;
      }

      try {
        setChartLoading(true);
        setErrorMsg("");
        const token = getToken();

        const firstDay = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
        const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
        const lastDay = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(totalDays).padStart(2, "0")}`;

        const response = await api.get("/admin/filter_attendance", {
          params: {
            employee_id: selectedEmployeeId,
            start_date: firstDay,
            end_date: lastDay,
          },
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!isCancelled && response.status === 200) {
          setAttendanceRecords(
            response.data.attendances || response.data.records || []
          );
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMsg(
            error.response?.data?.message || "Failed to fetch attendance data"
          );
          setAttendanceRecords([]);
        }
      } finally {
        if (!isCancelled) {
          setChartLoading(false);
        }
      }
    };

    fetchAttendance();

    return () => {
      isCancelled = true;
    };
  }, [selectedEmployeeId, selectedMonth, selectedYear]);

  // ==================== Build Chart Data ====================
  const chartData = useMemo(() => {
    const totalDays = new Date(selectedYear, selectedMonth, 0).getDate();
    const today = new Date();
    const isCurrentMonth =
      selectedMonth === today.getMonth() + 1 &&
      selectedYear === today.getFullYear();
    const lastDay = isCurrentMonth ? today.getDate() : totalDays;

    const hoursByDate = {};

    attendanceRecords.forEach((rec) => {
      if (!rec.check_in || !rec.check_out) return;

      const dateStr = new Date(rec.check_in).toISOString().split("T")[0];

      const checkIn = new Date(rec.check_in).getTime();
      const checkOut = new Date(rec.check_out).getTime();
      const grossMinutes = Math.floor((checkOut - checkIn) / 60000);
      const pauseMinutes = rec.total_pause_minutes || 0;
      const workedMinutes = Math.max(0, grossMinutes - pauseMinutes);

      hoursByDate[dateStr] = (hoursByDate[dateStr] || 0) + workedMinutes / 60;
    });

    const data = [];
    for (let d = 1; d <= lastDay; d++) {
      const dateObj = new Date(selectedYear, selectedMonth - 1, d);
      const dayOfWeek = dateObj.getDay();
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      data.push({
        day: d,
        label: String(d),
        fullDate: dateObj.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        isSunday: dayOfWeek === 0,
        Hours: Math.round((hoursByDate[dateStr] || 0) * 10) / 10,
      });
    }

    return data;
  }, [attendanceRecords, selectedMonth, selectedYear]);

  // ==================== Summary Stats ====================
  const summary = useMemo(() => {
    const workingDays = chartData.filter((d) => !d.isSunday && d.Hours > 0);
    const totalHours = chartData.reduce((sum, d) => sum + d.Hours, 0);
    const avgHours =
      workingDays.length > 0 ? totalHours / workingDays.length : 0;
    const maxHours =
      chartData.length > 0 ? Math.max(...chartData.map((d) => d.Hours)) : 0;

    return {
      totalHours: Math.round(totalHours * 10) / 10,
      avgHours: Math.round(avgHours * 10) / 10,
      maxHours: Math.round(maxHours * 10) / 10,
      daysWorked: workingDays.length,
    };
  }, [chartData]);

  const selectedEmployee = employees.find(
    (emp) =>
      emp.id === parseInt(selectedEmployeeId) ||
      emp.id === selectedEmployeeId
  );

  // ==================== Custom Tooltip ====================
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: "white",
            padding: "12px 16px",
            borderRadius: "10px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            border: "1px solid #e2e8f0",
            fontSize: "13px",
            minWidth: "150px",
          }}
        >
          <div
            style={{
              fontWeight: "700",
              color: "#1e293b",
              marginBottom: "6px",
              paddingBottom: "6px",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            {data.fullDate}
          </div>
          <div
            style={{
              color: data.Hours > 0 ? "#667eea" : "#94a3b8",
              fontWeight: "600",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span><FaClock style={{ marginRight: "6px" }} />Hours</span>
            <span>{data.Hours}h</span>
          </div>
          {data.isSunday && (
            <div
              style={{
                color: "#94a3b8",
                fontSize: "11px",
                marginTop: "4px",
                fontStyle: "italic",
              }}
            >
              Sunday (Off day)
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.Hours === 0) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="white"
        stroke="#667eea"
        strokeWidth={3}
      />
    );
  };

  const availableYears = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2,
  ];

  // ✅ Compact filter style
  const filterStyle = {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontWeight: "500",
    fontSize: "13px",
    color: "#1e293b",
    cursor: "pointer",
    outline: "none",
    height: "38px",
    boxSizing: "border-box",
  };

  return (
    <div className="students-card" style={{ marginBottom: "20px" }}>
      {/* Header */}
      <div
        className="card-header-custom"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "12px",
        }}
      >
        {/* Title Row */}
        <div>
          <h3 className="card-title" style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            <FaChartLine /> Member Attendance Chart
          </h3>
          <p
            className="card-subtitle"
            style={{ margin: "4px 0 0 0", fontSize: "13px" }}
          >
            View any member's daily working hours trend
          </p>
        </div>

        {/* ✅ Filters — single row on laptop, wrap on mobile */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{ ...filterStyle, width: "130px", flex: "0 0 auto" }}
          >
            <option value="all">All Roles</option>
            <option value="employee">Employees</option>
            <option value="intern">Interns</option>
            <option value="student">Students</option>
          </select>

          <select
            value={selectedEmployeeId}
            onChange={(e) => {
              userSelectedRef.current = true;
              setSelectedEmployeeId(e.target.value);
            }}
            style={{ ...filterStyle, minWidth: "180px", flex: "1 1 180px" }}
          >
            {filteredEmployees.length === 0 ? (
              <option value="">No members</option>
            ) : (
              filteredEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeName}
                </option>
              ))
            )}
          </select>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{ ...filterStyle, width: "130px", flex: "0 0 auto" }}
          >
            {monthNames.map((m, i) => (
              <option key={i} value={i + 1}>
                {m}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ ...filterStyle, width: "95px", flex: "0 0 auto" }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card-body-custom">
        {errorMsg && (
          <div className="error-message" style={{ marginBottom: "16px" }}>
            <FaTimesCircle style={{ marginRight: "6px" }} /> {errorMsg}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: "12px", color: "#888" }}>
              Loading members...
            </p>
          </div>
        ) : !selectedEmployeeId ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <div style={{ fontSize: "48px", marginBottom: "12px" }}><FaUsers /></div>
            <p style={{ color: "#888", fontSize: "15px", fontWeight: "600" }}>
              No member selected
            </p>
            <p style={{ color: "#94a3b8", fontSize: "13px" }}>
              Choose a role and member to view the chart
            </p>
          </div>
        ) : (
          <>
            {/* Selected Member Info */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "18px",
                padding: "12px 16px",
                background:
                  "linear-gradient(135deg, #eef2ff 0%, #f0f4ff 100%)",
                borderRadius: "12px",
                border: "1px solid #e0e7ff",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "700",
                  fontSize: "16px",
                }}
              >
                {selectedEmployee?.employeeName?.charAt(0) || "E"}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: "700",
                    color: "#1e293b",
                    fontSize: "15px",
                  }}
                >
                  {selectedEmployee?.employeeName}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    textTransform: "capitalize",
                  }}
                >
                  {selectedEmployee?.role} •{" "}
                  {selectedEmployee?.employee_fields || "—"} •{" "}
                  {monthNames[selectedMonth - 1]} {selectedYear}
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "12px",
                marginBottom: "18px",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                  border: "1px solid #c7d2fe",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "22px" }}><FaClock /></span>
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#4338ca",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Total Hours
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#3730a3",
                      lineHeight: "1.1",
                    }}
                  >
                    {summary.totalHours}h
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                  border: "1px solid #86efac",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "22px" }}><FaCalendarAlt /></span>
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#065f46",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Days Worked
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#047857",
                      lineHeight: "1.1",
                    }}
                  >
                    {summary.daysWorked}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "linear-gradient(135deg, #fef9c3, #fef08a)",
                  border: "1px solid #fde047",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "22px" }}><FaChartLine /></span>
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#854d0e",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Avg / Day
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#a16207",
                      lineHeight: "1.1",
                    }}
                  >
                    {summary.avgHours}h
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "linear-gradient(135deg, #fce7f3, #fbcfe8)",
                  border: "1px solid #f9a8d4",
                  borderRadius: "12px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "22px" }}>🔥</span>
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#9f1239",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Best Day
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#be185d",
                      lineHeight: "1.1",
                    }}
                  >
                    {summary.maxHours}h
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            {chartLoading ? (
              <div style={{ textAlign: "center", padding: "60px" }}>
                <div className="loading-spinner"></div>
                <p style={{ marginTop: "12px", color: "#888" }}>
                  Loading chart...
                </p>
              </div>
            ) : (
              <div style={{ width: "100%", height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={true}
                      horizontal={true}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#64748b" }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      label={{
                        value: "Hours",
                        angle: -90,
                        position: "insideLeft",
                        style: {
                          fontSize: "12px",
                          fill: "#94a3b8",
                          fontWeight: "600",
                        },
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }}
                      iconType="circle"
                    />
                    <Line
                      type="monotone"
                      dataKey="Hours"
                      stroke="#667eea"
                      strokeWidth={3}
                      dot={<CustomDot />}
                      activeDot={{ r: 7, fill: "#667eea" }}
                      name="Hours Worked"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAttendanceChart;